
import { reactive, computed } from '@nuxtjs/composition-api'

export default {
  provide() {
    return {
      wizard: reactive({
        steps: computed(() => this.mappedSteps),
        currentStepIndex: computed(() => this.currentStepIndex),
        currentStep: computed(() => this.currentStep),
        isFirstStep: computed(() => this.isFirstStep),
        isLastStep: computed(() => this.isLastStep),
        prevIndex: computed(() => this.prevIndex),
        prevRoute: computed(() => this.prevRoute),
        goPrev: this.goPrev,
        nextIndex: computed(() => this.nextIndex),
        nextRoute: computed(() => this.nextRoute),
        goNext: this.goNext,
        startOver: this.startOver,
      }),
    }
  },

  props: {
    steps: {
      type: Array,
      required: true,
    },

    redirectInitiallyIfInvalidRoute: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    mappedSteps() {
      return this.steps.map((step, i) => {
        if (typeof step.route !== 'object') {
          throw new TypeError(
            'Wizard step route has to be defined using the object syntax.'
          )
        }

        return {
          ...step,

          isFirst: i === 0,
          isLast: i === this.steps.length - 1,
          state:
            this.currentStepIndex > i
              ? 'prev'
              : this.currentStepIndex < i
              ? 'next'
              : 'active',
        }
      })
    },

    actualCurrentStepIndex() {
      const index = this.steps.findIndex((step) => {
        return this.localeRoute(step.route).name === this.$route.name
      })

      return index
    },

    // This step index is safe. It will not be an invalid step index no matter what.
    currentStepIndex() {
      if (this.actualCurrentStepIndex < 0) {
        return 0
      }

      if (this.actualCurrentStepIndex > this.steps.length - 1) {
        return this.steps.length - 1
      }

      return this.actualCurrentStepIndex
    },

    currentStep() {
      return this.steps[this.currentStepIndex]
    },

    isFirstStep() {
      return this.currentStepIndex === 0
    },

    isLastStep() {
      return this.currentStepIndex === this.steps.length - 1
    },

    prevIndex() {
      return this.isFirstStep ? null : this.currentStepIndex - 1
    },

    prevStep() {
      return this.prevIndex === null ? null : this.steps[this.prevIndex]
    },

    prevRoute() {
      return this.prevStep?.route
    },

    nextIndex() {
      return this.isLastStep ? null : this.currentStepIndex + 1
    },

    nextStep() {
      return this.nextIndex === null ? null : this.steps[this.nextIndex]
    },

    nextRoute() {
      return this.nextStep?.route
    },
  },

  created() {
    if (
      this.redirectInitiallyIfInvalidRoute &&
      this.actualCurrentStepIndex < 0
    ) {
      if (typeof route === 'string') {
        this.$router.replace({
          query: this.$route.query,
          path: this.steps[0].route,
        })
      } else {
        this.$router.replace({
          query: this.$route.query,
          ...this.steps[0].route,
        })
      }
    }
  },

  methods: {
    goPrev(params) {
      if (this.prevRoute) {
        this.go(this.prevRoute, params)
      }
    },

    goNext(params) {
      if (this.nextRoute) {
        this.go(this.nextRoute, params)
      }
    },

    startOver(params) {
      this.go(this.steps[0].route, params)
    },

    go(route, params) {
      if (typeof route === 'string') {
        this.$router.push(this.localeRoute({ path: route, params }))
      } else {
        this.$router.push(this.localeRoute({ ...route, params }))
      }
    },
  },

  render() {
    return this.$scopedSlots.default()
  },
}
